import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  CurrencyEur, Plus, Trash, PencilSimple, FloppyDisk, X, CircleNotch, Calendar, Buildings,
  CheckCircle, Warning,
} from "@phosphor-icons/react";
import { DatePicker } from "@/components/ui/date-picker";
import ResponsiveSheet from "@/components/ResponsiveSheet";
import { Pager, PAGE_SIZE } from "@/components/Pager";

const EMPTY_PERIOD = () => ({
  name: "",
  start_date: "",
  end_date: "",
  unit_ids: [],
  // NULL / prazno = ne diraj cijenu (period je restriction_only na save-u).
  // Broj (>0) = postavi tu cijenu kao anchor za odabrani rate plan.
  base_price: null,
  extra_guest_price: null,
  channels: {},
  // Restrictions (per-rate when rate_plan_id is set)
  min_stay: 1,
  max_stay: null,
  min_advance_days: 0,
  max_advance_days: null,
  no_arrival: false,
  no_departure: false,
  stop_sell: false,
  // Legacy alias — kept in sync with min_stay on the backend
  min_nights: 1,
  max_nights: null,
  priority: 0,
  color: "#11A285",
  active: true,
  // Draft mode default: new periods start as drafts so the user can review
  // all of them and push ONCE to Channex — mirrors the calendar's Draft Mode
  // pattern and required for certification tests #3-#8.
  is_draft: true,
  // Cascade isolation (Test #8): when TRUE the change applies to ONLY the selected
  // rate plan and OTHER rate plans of the same room type keep their current prices
  // (backend auto-creates a "lock" period for each sibling rate plan). When FALSE
  // (default) a base_price change on BAR cascades to derived plans (BB/HB/NR)
  // through the modifier formula (BB = BAR + 20 % etc.).
  cascade_isolate: false,
  // When true, this period ONLY applies restrictions (min_stay, CTA, stop_sell...)
  // and the base_price / channels are IGNORED by sync worker. Auto-inferred at save-time
  // from empty base_price — user više NE mora ručno biti kliknuti toggle.
  restriction_only: false,
});

function fmtShort(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y.slice(2)}`;
}

export default function PricingPage() {
  const [periods, setPeriods] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlansByRt, setRatePlansByRt] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [draftPanelOpen, setDraftPanelOpen] = useState(false);
  const [error, setError] = useState("");

  const drafts = useMemo(() => periods.filter((p) => p.is_draft), [periods]);
  const [page, setPage] = useState(1);
  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || ""))),
    [periods],
  );

  const publishAllDrafts = async () => {
    if (drafts.length === 0) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/rate-periods/publish-drafts");
      const msg = `Objavljeno ${data.published} draft periodo${data.published === 1 ? "" : "va"} · ${data.units_enqueued} jedinica sinkronizirano · ${(data.kinds || []).length === 3 ? "2" : "1"} OTA sync poziv${(data.kinds || []).length === 3 ? "a" : ""}`;
      alert(msg);
      setDraftPanelOpen(false);
      await load();
    } catch (e) {
      alert(`Nije uspjelo: ${formatApiError(e)}`);
    } finally {
      setPublishing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, u, pr, rt] = await Promise.all([
        api.get("/rate-periods"),
        api.get("/units"),
        api.get("/properties"),
        api.get("/room-types"),
      ]);
      setPeriods(p.data);
      setUnits(u.data);
      setProperties(pr.data);
      setRoomTypes(rt.data);
      // Fetch rate plans for every room type in parallel — collapses to one small dict.
      const plansEntries = await Promise.all(
        rt.data.map(async (rtDoc) => {
          try {
            const rr = await api.get(`/room-types/${rtDoc.id}/rate-plans`);
            return [rtDoc.id, rr.data || []];
          } catch { return [rtDoc.id, []]; }
        })
      );
      setRatePlansByRt(Object.fromEntries(plansEntries));
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const findRatePlan = (id) => {
    for (const [rtId, plans] of Object.entries(ratePlansByRt)) {
      const hit = plans.find((p) => p.id === id);
      if (hit) return { ...hit, roomTypeId: rtId };
    }
    return null;
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      if (!editing.start_date || !editing.end_date) {
        setError("Odaberi datumski raspon.");
        setSaving(false);
        return;
      }
      if (editing.start_date > editing.end_date) {
        setError("Početni datum mora biti prije ili jednak završnom.");
        setSaving(false);
        return;
      }
      // AUTO-INFER: prazno base_price polje = period je "restriction_only".
      // U tom slučaju cijena na kalendaru ostaje NEPROMIJENJENA — sync worker
      // ignorira base_price i channels, primjenjuje samo min_stay/CTA/stop_sell itd.
      // Ako je base_price broj > 0, radi se o punom cjenovnom periodu.
      const priceEmpty = editing.base_price === null || editing.base_price === "" || Number(editing.base_price) === 0;
      const fmt = (iso) => { const [, m, d] = iso.split("-"); return `${Number(d)}.${Number(m)}.`; };
      // New period with several rate plans selected → one period per rate plan
      const targets = !editing.id && (editing.rate_plan_ids || []).length > 0 ? editing.rate_plan_ids : [editing.rate_plan_id || null];
      for (const rpId of targets) {
      const rp = rpId ? findRatePlan(rpId) : null;
      const autoName = `${rp ? (rp.is_default ? "BAR" : rp.name) : "Svi"} ${fmt(editing.start_date)}–${fmt(editing.end_date)}`;
      const { rate_plan_ids: _ignored, ...base } = editing;
      const payload = {
        ...base,
        rate_plan_id: rpId,
        name: (editing.name || "").trim() || autoName,
        priority: 100,
        active: true,
        channels: {},
        extra_guest_price: null,
        unit_ids: rp ? units.filter((u) => u.room_type_id === rp.roomTypeId && u.active !== false).map((u) => u.id) : [],
        base_price: priceEmpty ? 0 : Number(editing.base_price),
        restriction_only: priceEmpty,
        // Cascade isolate only makes sense when a specific rate plan is chosen AND a price is set.
        cascade_isolate: !priceEmpty && !!rpId && !!editing.cascade_isolate,
      };
      if (editing.id) {
        await api.put(`/rate-periods/${editing.id}`, payload);
      } else {
        await api.post("/rate-periods", payload);
      }
      }
      if (targets.length > 1) toast.success(`Spremljeno na ${targets.length} rate planova`);
      setEditing(null);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Obrisati cjenovni period?")) return;
    try {
      await api.delete(`/rate-periods/${id}`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const purgeHistory = async () => {
    if (!confirm("Obrisati sve istekle cjenovne periode (završili prije danas)? Buduće cijene ostaju netaknute.")) return;
    try {
      const { data } = await api.delete("/rate-periods/history");
      toast.success(`Obrisano ${data.deleted} isteklih perioda`);
      setPage(1);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="hidden font-heading text-2xl font-bold tracking-tight text-foreground sm:block">Cijene</h1>
          <p className="hidden text-sm text-muted-foreground sm:mt-1 sm:block">
            Cjenovni periodi za BAR — izvedeni rate planovi (Booking.com, Airbnb…) računaju se automatski.
          </p>
        </div>
        <button
          onClick={() => setEditing(EMPTY_PERIOD())}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          data-testid="pricing-new-btn"
        >
          <Plus size={14} weight="bold" /> Novi cjenovni period
        </button>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-start gap-2 text-xs text-muted-foreground">
          <Warning size={14} weight="duotone" className="text-warning shrink-0 mt-0.5" />
          <span>Datumi bez cjenovnog perioda nisu dostupni za rezervaciju. Kod preklapanja vrijedi <strong>zadnje spremljeni</strong> period.</span>
        </div>
      </div>

      {periods.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <CurrencyEur size={36} className="mx-auto text-muted-foreground" />
          <p className="mt-3 font-medium text-foreground">Još nema cjenovnih perioda</p>
          <p className="mt-1 text-xs text-muted-foreground">Nijedan datum nije otvoren za rezervaciju. Kreiraj prvi period klikom na <strong>Novi cjenovni period</strong> gore.</p>
        </div>
      ) : (
        <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Pager page={page} total={sortedPeriods.length} onChange={setPage} testId="pricing-pager" />
          <button onClick={purgeHistory} className="ml-auto rounded-md border border-border px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10" data-testid="pricing-purge-history">
            Obriši povijest (istekli periodi)
          </button>
        </div>
        <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card" data-testid="pricing-list">
          {sortedPeriods.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((p) => {
            const rp = p.rate_plan_id ? findRatePlan(p.rate_plan_id) : null;
            const rt = rp ? roomTypes.find((r) => r.id === rp.roomTypeId) : null;
            const scope = rp ? `${rt?.name || ""} · ${rp.is_default ? "BAR" : rp.name}` : "Svi rate planovi";
            return (
              <div key={p.id} className="flex items-center gap-3 px-3 py-3 sm:px-4" data-testid={`pricing-row-${p.id}`}>
                <span className="h-8 w-1 shrink-0 rounded-full" style={{ background: p.color || "hsl(var(--primary))" }} />
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-semibold text-foreground">{p.name}</span>
                    {p.is_draft && <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:text-amber-300" data-testid={`pricing-draft-badge-${p.id}`}>Draft</span>}
                  </div>
                  <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="shrink-0 font-mono">{fmtShort(p.start_date)} – {fmtShort(p.end_date)}</span>
                    <span className="shrink-0">·</span>
                    <span className="truncate">{scope}</span>
                  </div>
                </div>
                <div className="shrink-0 text-right font-mono text-sm font-semibold text-foreground">
                  {p.restriction_only ? <span className="text-xs font-medium text-muted-foreground">Ograničenja</span> : <>€{Number(p.base_price).toFixed(0)}</>}
                </div>
                <div className="flex shrink-0 items-center">
                  <button onClick={() => setEditing({ ...EMPTY_PERIOD(), ...p })} className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`pricing-edit-${p.id}`}>
                    <PencilSimple size={15} />
                  </button>
                  <button onClick={() => remove(p.id)} className="rounded-md p-2 text-destructive hover:bg-destructive/10" data-testid={`pricing-delete-${p.id}`}>
                    <Trash size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <Pager page={page} total={sortedPeriods.length} onChange={setPage} testId="pricing-pager-bottom" />
        </div>
      )}

      {editing && (
        <PeriodForm
          period={editing}
          setPeriod={setEditing}
          units={units}
          properties={properties}
          roomTypes={roomTypes}
          ratePlansByRt={ratePlansByRt}
          saving={saving}
          error={error}
          onClose={() => { setEditing(null); setError(""); }}
          onSave={save}
        />
      )}

      {/* Draft trigger + slide-out panel (mirrors CalendarPage.jsx pattern) */}
      <PricingDraftTrigger
        count={drafts.length}
        onOpen={() => setDraftPanelOpen(true)}
        hidden={draftPanelOpen || !!editing}
      />
      <PricingDraftPanel
        open={draftPanelOpen}
        drafts={drafts}
        rateplansByRt={ratePlansByRt}
        roomTypes={roomTypes}
        onClose={() => setDraftPanelOpen(false)}
        onEdit={(p) => { setEditing({ ...EMPTY_PERIOD(), ...p }); setDraftPanelOpen(false); }}
        onRemove={remove}
        onPublish={publishAllDrafts}
        publishing={publishing}
      />
    </div>
  );
}

function RatePlanMultiSelect({ options, value, onChange }) {
  const byRt = {};
  options.forEach((rp) => { (byRt[rp.roomTypeId] ||= { name: rp.roomTypeName, property: rp.propertyName, plans: [] }).plans.push(rp); });
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const allBar = options.filter((rp) => rp.is_default).map((rp) => rp.id);
  return (
    <div data-testid="period-rate-plans-multi">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rate planovi</span>
        <div className="flex gap-2 text-[11px]">
          <button type="button" onClick={() => onChange(allBar)} className="font-medium text-primary hover:underline" data-testid="period-rp-all-bar">Svi BAR</button>
          <button type="button" onClick={() => onChange(options.map((o) => o.id))} className="font-medium text-primary hover:underline" data-testid="period-rp-all">Svi</button>
          <button type="button" onClick={() => onChange([])} className="font-medium text-muted-foreground hover:underline" data-testid="period-rp-none">Očisti</button>
        </div>
      </div>
      <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border border-border p-2">
        {Object.entries(byRt).map(([rtId, g]) => (
          <div key={rtId}>
            <p className="truncate text-[11px] font-semibold text-foreground">{g.property} · {g.name}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {g.plans.map((rp) => {
                const on = value.includes(rp.id);
                return (
                  <button key={rp.id} type="button" onClick={() => toggle(rp.id)} className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-muted"}`} data-testid={`period-rp-${rp.id}`}>
                    {rp.is_default ? "★ " : ""}{rp.code || rp.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{value.length === 0 ? "Ništa odabrano = primjenjuje se na sve rate planove." : `Odabrano: ${value.length}`}</p>
    </div>
  );
}

function PeriodForm({ period, setPeriod, units, properties, roomTypes, ratePlansByRt, saving, error, onClose, onSave }) {
  const update = (k, v) => setPeriod({ ...period, [k]: v });

  const ratePlanOptions = useMemo(() => {
    const propertyById = Object.fromEntries((properties || []).map((p) => [p.id, p]));
    const rows = [];
    const seen = new Set();
    for (const rt of roomTypes) {
      const prop = propertyById[rt.property_id];
      if (!prop) continue;
      for (const rp of ratePlansByRt[rt.id] || []) {
        if (seen.has(rp.id)) continue;
        seen.add(rp.id);
        rows.push({ ...rp, roomTypeId: rt.id, roomTypeName: rt.name, propertyName: prop.name });
      }
    }
    return rows;
  }, [roomTypes, ratePlansByRt, properties]);

  const onStartChange = (v) => {
    const next = { ...period, start_date: v };
    if (v && (!period.end_date || period.end_date < v)) next.end_date = v;
    setPeriod(next);
  };

  const hasPrice = period.base_price !== null && period.base_price !== "" && Number(period.base_price) > 0;
  const nights = period.start_date && period.end_date
    ? Math.round((new Date(period.end_date) - new Date(period.start_date)) / 86400000) + 1 : 0;

  const toggles = [
    { k: "no_arrival", label: "Zatvoreno za dolaske", short: "CTA", testId: "period-no-arrival" },
    { k: "no_departure", label: "Zatvoreno za odlaske", short: "CTD", testId: "period-no-departure" },
    { k: "stop_sell", label: "Zaustavi prodaju", short: "Stop sell", testId: "period-stop-sell", danger: true },
  ];

  return (
    <ResponsiveSheet
      onClose={onClose}
      title={period.id ? "Uredi cjenovni period" : "Novi cjenovni period"}
      subtitle={nights > 0 ? `${nights} ${nights === 1 ? "dan" : "dana"}` : "Odaberi datume i cijenu"}
      width={640}
      testId="pricing-form"
      footer={
        <>
          <label className="mr-auto flex min-w-0 cursor-pointer items-center gap-2 text-sm text-foreground" title="Draft se ne šalje na OTA dok ga ne objaviš">
            <input type="checkbox" className="h-4 w-4 shrink-0" checked={!!period.is_draft} onChange={(e) => update("is_draft", e.target.checked)} style={{ accentColor: "hsl(var(--primary))" }} data-testid="period-is-draft" />
            <span className="truncate">Spremi kao draft</span>
          </label>
          <button onClick={onClose} className="h-10 shrink-0 rounded-md border border-border px-4 text-sm font-medium hover:bg-muted">Odustani</button>
          <button onClick={onSave} disabled={saving} className="flex h-10 shrink-0 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="pricing-save-btn">
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="duotone" />}
            Spremi
          </button>
        </>
      }
    >
      <div className="space-y-5 p-4 sm:p-5">
        {!period.id ? (
          <RatePlanMultiSelect options={ratePlanOptions} value={period.rate_plan_ids || []} onChange={(ids) => setPeriod({ ...period, rate_plan_ids: ids, rate_plan_id: ids.length === 1 ? ids[0] : null })} />
        ) : (
        <Field label="Rate plan">
          <select className="rw-input" value={period.rate_plan_id || ""} onChange={(e) => update("rate_plan_id", e.target.value || null)} data-testid="period-rate-plan">
            <option value="">Svi rate planovi</option>
            {ratePlanOptions.filter((rp) => rp.is_default).length > 0 && (
              <optgroup label="BAR">
                {ratePlanOptions.filter((rp) => rp.is_default).map((rp) => (
                  <option key={rp.id} value={rp.id}>{rp.propertyName} · {rp.roomTypeName}</option>
                ))}
              </optgroup>
            )}
            {ratePlanOptions.filter((rp) => !rp.is_default).length > 0 && (
              <optgroup label="Izvedeni rate planovi">
                {ratePlanOptions.filter((rp) => !rp.is_default).map((rp) => (
                  <option key={rp.id} value={rp.id}>{rp.roomTypeName} · {rp.name}</option>
                ))}
              </optgroup>
            )}
          </select>
        </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Od" required>
            <DatePicker value={period.start_date || ""} onChange={onStartChange} placeholder="Početak" className="h-10" data-testid="period-start" />
          </Field>
          <Field label="Do (uključivo)" required>
            <DatePicker value={period.end_date || ""} onChange={(v) => update("end_date", v)} minDate={period.start_date || undefined} placeholder="Kraj" className="h-10" data-testid="period-end" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cijena / noć (€)">
            <input
              type="number" step="0.01" min="0" inputMode="decimal"
              className="rw-input mono"
              value={period.base_price ?? ""}
              onChange={(e) => update("base_price", e.target.value === "" ? null : parseFloat(e.target.value))}
              placeholder="Bez promjene"
              data-testid="period-base-price"
            />
          </Field>
          <Field label="Naziv (opcionalno)">
            <input className="rw-input" value={period.name || ""} onChange={(e) => update("name", e.target.value)} placeholder="Automatski" data-testid="period-name" />
          </Field>
        </div>

        {(period.rate_plan_id || (period.rate_plan_ids || []).length > 0) && hasPrice && (
          <label className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-muted/30 px-3 py-2.5 text-sm" data-testid="period-cascade-isolate-wrapper">
            <input type="checkbox" className="h-4 w-4 shrink-0" checked={!!period.cascade_isolate} onChange={(e) => update("cascade_isolate", e.target.checked)} style={{ accentColor: "hsl(var(--primary))" }} data-testid="period-cascade-isolate" />
            <span className="min-w-0 truncate text-foreground">Ne mijenjaj izvedene rate planove</span>
          </label>
        )}

        <div data-testid="period-restrictions">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Ograničenja</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Min noći">
              <input type="number" min="1" inputMode="numeric" className="rw-input mono" value={period.min_stay ?? period.min_nights ?? 1}
                onChange={(e) => { const v = parseInt(e.target.value) || 1; setPeriod({ ...period, min_stay: v, min_nights: v }); }} data-testid="period-min-stay" />
            </Field>
            <Field label="Max noći">
              <input type="number" min="0" inputMode="numeric" className="rw-input mono" value={period.max_stay ?? period.max_nights ?? ""} placeholder="—"
                onChange={(e) => { const v = e.target.value === "" ? null : parseInt(e.target.value) || null; setPeriod({ ...period, max_stay: v, max_nights: v }); }} data-testid="period-max-stay" />
            </Field>
            <Field label="Min unaprijed">
              <input type="number" min="0" inputMode="numeric" className="rw-input mono" value={period.min_advance_days ?? 0} onChange={(e) => update("min_advance_days", parseInt(e.target.value) || 0)} />
            </Field>
            <Field label="Max unaprijed">
              <input type="number" min="0" inputMode="numeric" className="rw-input mono" value={period.max_advance_days ?? ""} placeholder="—" onChange={(e) => update("max_advance_days", e.target.value === "" ? null : parseInt(e.target.value) || null)} />
            </Field>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {toggles.map((t) => {
              const on = !!period[t.k];
              return (
                <button
                  key={t.k} type="button" onClick={() => update(t.k, !on)} title={t.label}
                  className={`flex h-10 items-center justify-center rounded-md border px-2 text-xs font-semibold transition-colors ${on ? (t.danger ? "border-destructive bg-destructive text-destructive-foreground" : "border-primary bg-primary text-primary-foreground") : "border-border bg-card text-muted-foreground hover:bg-muted"}`}
                  aria-pressed={on} data-testid={t.testId}
                >
                  <span className="truncate">{t.short}</span>
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <Warning size={14} weight="duotone" className="shrink-0" /> <span className="min-w-0">{error}</span>
          </div>
        )}
      </div>
      <style>{`.rw-input { width:100%; height:2.5rem; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0 0.75rem; font-size:0.875rem; border-radius:var(--radius); color:hsl(var(--foreground)); } .rw-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary)/0.2); }`}</style>
    </ResponsiveSheet>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="ml-0.5 text-destructive">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block truncate text-[10px] text-muted-foreground">{hint}</span>}
    </label>
  );
}


// -----------------------------------------------------------------------------
// Draft Mode UI for rate periods — floating trigger + right-side slide-out
// panel. Publishes all drafts atomically via POST /rate-periods/publish-drafts
// so the whole batch reaches Channex as ONE API call (or 2 if stop_sell forces
// availability). Same UX pattern as CalendarPage's Draft Mode but backed by
// DB flags (is_draft=true) rather than localStorage, because rate periods are
// first-class DB rows the user wants to edit/keep even before publishing.
// -----------------------------------------------------------------------------
function PricingDraftTrigger({ count, onOpen, hidden }) {
  if (hidden || count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="rw-sheet-panel fixed bottom-20 right-4 z-40 flex h-11 items-center gap-2 rounded-full bg-amber-600 px-4 text-sm font-semibold text-white shadow-xl hover:bg-amber-700 lg:bottom-5 lg:right-5"
      title={`${count} nespremljenih draftova — klikni za pregled`}
      data-testid="pricing-draft-trigger"
    >
      <PencilSimple size={16} weight="bold" />
      <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold" data-testid="pricing-draft-count">{count}</span>
    </button>
  );
}

function _formatDraftScope(d, roomTypes, ratePlansByRt) {
  const bits = [];
  if (d.rate_plan_id) {
    // Locate rate plan across all RTs
    for (const rtId of Object.keys(ratePlansByRt || {})) {
      const rp = (ratePlansByRt[rtId] || []).find((r) => r.id === d.rate_plan_id);
      if (rp) {
        const rt = roomTypes.find((r) => r.id === rtId);
        bits.push(`${rt?.name || ""} · ★ ${rp.name}`);
        break;
      }
    }
  } else {
    bits.push("Svi rate planovi");
  }
  bits.push(d.unit_ids?.length ? `${d.unit_ids.length} jed.` : "sve jed.");
  return bits.filter(Boolean).join(" · ");
}

function PricingDraftPanel({ open, drafts, rateplansByRt, roomTypes, onClose, onEdit, onRemove, onPublish, publishing }) {
  if (!open) return null;
  const count = drafts.length;
  return (
    <>
      <button
        type="button"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]"
        aria-label="Zatvori draft panel"
        data-testid="pricing-draft-panel-backdrop"
      />
      <aside
        className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full max-w-md flex-col border-l border-border bg-card shadow-2xl"
        data-testid="pricing-draft-panel"
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Draftovi cjenovnih perioda</p>
            <h2 className="text-lg font-semibold text-foreground" data-testid="pricing-draft-panel-count">
              {count} {count === 1 ? "period" : "perioda"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Zatvori"
            data-testid="pricing-draft-panel-close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {count === 0 ? (
            <div className="mt-16 text-center">
              <p className="text-sm text-muted-foreground">Nema draftova.</p>
              <p className="mt-2 text-xs text-muted-foreground/70">Kreiraj cjenovni period — po defaultu se sprema kao draft dok ga ne objaviš.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {drafts.map((d) => {
                const scope = _formatDraftScope(d, roomTypes, rateplansByRt);
                const dateStr = d.start_date === d.end_date
                  ? d.start_date
                  : `${d.start_date} → ${d.end_date}`;
                const restrictionBits = [];
                if (d.min_stay && d.min_stay > 1) restrictionBits.push(`min ${d.min_stay}n`);
                if (d.max_stay) restrictionBits.push(`max ${d.max_stay}n`);
                if (d.no_arrival) restrictionBits.push("CTA");
                if (d.no_departure) restrictionBits.push("CTD");
                if (d.stop_sell) restrictionBits.push("STOP");
                return (
                  <li
                    key={d.id}
                    className="group flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 shadow-sm"
                    data-testid={`pricing-draft-item-${d.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="rounded bg-amber-600/20 px-1.5 py-0.5 font-mono font-semibold text-amber-700 dark:text-amber-300">{dateStr}</span>
                        <span className="text-foreground font-medium truncate">{d.name || "(bez naziva)"}</span>
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">{scope}</div>
                      <div className="mt-1.5 flex flex-wrap items-baseline gap-2 text-xs">
                        {Number(d.base_price) > 0 && (
                          <span className="font-mono font-bold text-foreground">€{Number(d.base_price).toFixed(2)}</span>
                        )}
                        {restrictionBits.length > 0 && (
                          <span className="rounded bg-muted/60 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                            {restrictionBits.join(" · ")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => onEdit(d)}
                        className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                        title="Uredi draft"
                        data-testid={`pricing-draft-edit-${d.id}`}
                      >
                        <PencilSimple size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemove(d.id)}
                        className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        title="Ukloni draft"
                        data-testid={`pricing-draft-remove-${d.id}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={onPublish}
            disabled={count === 0 || publishing}
            className="w-full rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-40"
            data-testid="pricing-draft-publish"
          >
            {publishing
              ? <><CircleNotch size={14} className="inline animate-spin" /> Objavljujem…</>
              : <>Spremi promjene</>}
          </button>
          <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground/80">
            Svi draftovi se objavljuju odjednom → točno <span className="font-mono">1 API poziv</span> (ili 2 ako neki ima Stop Sell). Task ID vidljiv u <span className="font-medium">Kanali → Sync aktivnost</span>.
          </p>
        </footer>
      </aside>
    </>
  );
}

